<template>
  <article v-if="post && Object.keys(post).length">
    <PostFeaturedImage
      v-if="post.featuredImage"
      :is-aspect="true"
      :is-wide="post.featuredImageWidth === 'wide'"
      :featured-image="post.featuredImage.node"
      :srcset="post.imageSrcset"
      :ga4-data="
        JSON.stringify({
          click_type: 'hero',
          component: 'ArticlePost',
          content_group: 'post',
          gtm_tag: 'image',
          file_name: `${post.featuredImage.node.sourceUrl.split('/').pop()}`,
        })
      "
    ></PostFeaturedImage>
    <article :class="[$style.article, 'container']">
      <header>
        <nav>
          <BackLink
            component="ArticlePost"
            group="post"
            label="Articles"
            name="ArticleSection"
          ></BackLink>
        </nav>
        <h1 v-html="post.title"></h1>
        <div :class="[$style.categories, 'subtitle-2', 'space-0-above']">
          <ul>
            <li v-for="(cat, index) in post.categories.nodes" :key="index">
              <router-link
                :to="{
                  name: 'ArticleTerm',
                  params: {
                    slug: category,
                    subcategory: getSubCategory(cat.slug),
                    type: 'category',
                  },
                }"
                :ga4-event="
                  JSON.stringify({
                    filter_location: 'post',
                    filter_type: 'category',
                    filter_value: `${cat.name}`,
                    gtm_tag: 'filter',
                  })
                "
                v-html="cat.name"
              ></router-link>
            </li>
          </ul>
          <time class="subtitle-2" :datetime="post.date">{{ post.date }}</time>
        </div>
        <ArticleByline :author-data="post.coAuthors"></ArticleByline>
      </header>
      <ProcessContent
        ref="mainContent"
        :content="post.content"
        wrapper="section"
        :class="[$style.content, 'space-12-16-above']"
      ></ProcessContent>
      <footer v-if="post.tags.nodes.length" class="space-24-32-above">
        <p class="label">Tags:</p>
        <ul class="tags">
          <li v-for="(tag, index) in post.tags.nodes" :key="index">
            <router-link
              :to="{
                name: 'ArticleTerm',
                params: {
                  slug: tag.slug,
                  type: 'tag',
                },
              }"
              :ga4-event="
                JSON.stringify({
                  filter_location: 'post',
                  filter_type: 'tag',
                  filter_value: `${tag.name}`,
                  gtm_tag: 'filter',
                })
              "
              v-html="tag.name"
            ></router-link>
          </li>
        </ul>
      </footer>
    </article>
  </article>
</template>

<script setup lang="ts">
import ArticleByline from '@/components/article/ArticleByline.vue';
import { articlePostQuery } from '@/queries/GetArticlePost';
import BackLink from '@/components/common/BackLink.vue';
import { dataLoaded, useHandleError, useMetaData } from '@/composables/Common.js';
import { Post } from '@/types/articles.interface';
import PostFeaturedImage from '@/components/common/PostFeaturedImage.vue';
import ProcessContent from '@/components/common/ProcessContent.vue';
import { ref } from 'vue';
import { useQuery } from 'villus';

const props = defineProps<{
  category: string;
  slug: string;
}>();

const { handleError } = useHandleError();
const { setMetaDescription, setMetaTitle } = useMetaData();

const post = ref<Post>({});

const { data } = useQuery({
  onData: (data) => {
    if (data.post && Object.keys(data.post).length) {
      post.value = data.post;
      setMetaTitle(data.title);
      setMetaDescription(data.excerpt);
      dataLoaded();
    }

    if (!data.post || Object.keys(data.post).length === 0) {
      handleError('ArticlePost', props.slug);
    }
  },
  query: articlePostQuery,
  variables: { slug: props.slug },
});

function getSubCategory(slug: string): string {
  if (props.category !== slug) {
    return slug;
  }

  return '';
}
</script>

<script lang="ts">
export default {
  beforeRoute(params, villusClient) {
    return new Promise<void>((resolve, reject) => {
      villusClient
        .executeQuery({
          query: articlePostQuery,
          variables: {
            slug: params.slug,
          },
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  },
};
</script>

<style lang="scss">
@use '@/styles/links.module.scss' as *;
</style>

<style lang="scss" module>
.categories {
  time {
    display: inline;
  }

  ul {
    display: inline;
    margin: 0;
    padding: 0;

    li {
      display: inline-block;

      &::after {
        @include pipe;
      }

      a {
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

.content {
  & > img {
    height: 100%;
    width: 100%;
  }
}

@media (min-width: $break-point) {
  .article {
    @include grid;

    footer {
      grid-column: 1 / span 6;
    }

    > header {
      @include grid;
      grid-column: span 12;

      > * {
        grid-column: 1 / span 8;
      }

      nav {
        grid-column: span 12;
      }
    }

    .content {
      @include grid-content;
    }
  }
}
</style>
